/**
 * Outline of a group of detections, as polygon rings.
 *
 * Two requirements pull against each other: the outline must bridge the gaps
 * between the detections of one fire, and it must not stand off from them. A
 * plain dilation by delta does the first but not the second — it puts a
 * delta-wide envelope around everything. Eroding that dilation back by delta
 * removes the envelope but takes the bridges with it, collapsing a
 * single-pixel-wide fire front to nothing.
 *
 * So the bridging is done along the *connections* instead of by inflating
 * every point: detections within 2·delta are linked, and the outline is the
 * union of thin sleeves around the detections and their links. Delta then
 * decides only what belongs to the same fire; the width of the shape is the
 * sensor footprint — a VIIRS pixel is ~375 m across, so ~190 m either side of
 * a detection is the ground it actually covers.
 *
 * Extracted with marching squares over a distance field: no dependency, no
 * polygon-clipping edge cases, and the contour is smooth because cell edges
 * are interpolated.
 *
 * All maths is in a local metric frame (metres); callers convert to lon/lat.
 */

export interface MetricPoint {
  x: number;
  y: number;
}

/**
 * Half-width of the outline: the ground one detection covers. This is not an
 * envelope — it does not scale with delta.
 */
export const FOOTPRINT_M = 190;
/** Cells across the footprint. Higher = smoother, more work. */
const CELLS_PER_FOOTPRINT = 3;
/** Bounds the grid for a cluster sprawling over a large area. */
const MAX_GRID_DIM = 600;
/** Links per detection. Enough to fill a dense blob, few enough to stay fast. */
const MAX_LINKS_PER_POINT = 8;

type Ring = MetricPoint[];

// Quantize to millimetres to absorb the ~1e-14 differences between the two
// cells sharing an edge. Math.round (not toFixed) matters: toFixed keeps the
// sign of negative zero, so a crossing at exactly 0 would key as "-0.000" from
// one cell and "0.000" from the other and the ring would never close.
const key = (p: MetricPoint) =>
  `${Math.round(p.x * 1000)}:${Math.round(p.y * 1000)}`;

/** Linear interpolation of the iso-crossing between two corners. */
const interp = (
  ax: number,
  ay: number,
  av: number,
  bx: number,
  by: number,
  bv: number
): MetricPoint => {
  const denom = av - bv;
  const t = Math.abs(denom) < 1e-12 ? 0.5 : av / denom;
  return { x: ax + (bx - ax) * t, y: ay + (by - ay) * t };
};

export const ringArea = (ring: Ring): number => {
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
};

const pointSegmentDistance = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
) => {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
};

/** Links between detections of one fire: nearest neighbours within 2·delta. */
const buildLinks = (points: MetricPoint[], delta: number): [number, number][] => {
  const reach = delta * 2;
  const links: [number, number][] = [];
  const seen = new Set<string>();

  for (let i = 0; i < points.length; i++) {
    const near: { j: number; d: number }[] = [];
    for (let j = 0; j < points.length; j++) {
      if (i === j) continue;
      const d = Math.hypot(points[i].x - points[j].x, points[i].y - points[j].y);
      if (d <= reach) near.push({ j, d });
    }
    near.sort((a, b) => a.d - b.d);
    for (const { j } of near.slice(0, MAX_LINKS_PER_POINT)) {
      const k = i < j ? `${i}:${j}` : `${j}:${i}`;
      if (seen.has(k)) continue;
      seen.add(k);
      links.push([i, j]);
    }
  }
  return links;
};

/**
 * Outlines of a group of detections: sleeves of half-width *footprint* around
 * the detections and the links between those within 2·delta.
 *
 * Returns every outer ring — detections too far apart to link come back as
 * separate rings, and the caller treats each as its own fire. Interior holes
 * are discarded: the simulator rasterizes a polygon by filling its exterior
 * ring and ignores interior rings anyway.
 */
export const detectionOutlineRings = (
  points: MetricPoint[],
  delta: number,
  footprint: number = FOOTPRINT_M
): Ring[] => {
  if (!points.length || footprint <= 0) return [];

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const pad = footprint * 2;
  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;

  // Resolution follows the footprint, not delta: the sleeve is footprint-wide
  // and would fall between samples if cells were sized by delta.
  let cell = footprint / CELLS_PER_FOOTPRINT;
  const width = maxX - minX;
  const height = maxY - minY;
  cell = Math.max(cell, width / MAX_GRID_DIM, height / MAX_GRID_DIM);

  const nx = Math.max(2, Math.ceil(width / cell));
  const ny = Math.max(2, Math.ceil(height / cell));

  const field = new Float64Array((nx + 1) * (ny + 1)).fill(Infinity);
  const at = (ix: number, iy: number) => iy * (nx + 1) + ix;

  const stampPoint = (p: MetricPoint) => {
    const reach = Math.ceil(footprint / cell) + 2;
    const cx = Math.round((p.x - minX) / cell);
    const cy = Math.round((p.y - minY) / cell);
    for (let iy = Math.max(0, cy - reach); iy <= Math.min(ny, cy + reach); iy++) {
      for (let ix = Math.max(0, cx - reach); ix <= Math.min(nx, cx + reach); ix++) {
        const d = Math.hypot(minX + ix * cell - p.x, minY + iy * cell - p.y);
        const index = at(ix, iy);
        if (d < field[index]) field[index] = d;
      }
    }
  };

  const stampSegment = (a: MetricPoint, b: MetricPoint) => {
    const margin = footprint + cell * 2;
    const ix0 = Math.max(0, Math.floor((Math.min(a.x, b.x) - margin - minX) / cell));
    const ix1 = Math.min(nx, Math.ceil((Math.max(a.x, b.x) + margin - minX) / cell));
    const iy0 = Math.max(0, Math.floor((Math.min(a.y, b.y) - margin - minY) / cell));
    const iy1 = Math.min(ny, Math.ceil((Math.max(a.y, b.y) + margin - minY) / cell));
    for (let iy = iy0; iy <= iy1; iy++) {
      for (let ix = ix0; ix <= ix1; ix++) {
        const d = pointSegmentDistance(
          minX + ix * cell,
          minY + iy * cell,
          a.x,
          a.y,
          b.x,
          b.y
        );
        const index = at(ix, iy);
        if (d < field[index]) field[index] = d;
      }
    }
  };

  for (const p of points) stampPoint(p);
  for (const [i, j] of buildLinks(points, delta)) {
    stampSegment(points[i], points[j]);
  }
  for (let i = 0; i < field.length; i++) field[i] -= footprint;

  // Marching squares: collect boundary segments, then stitch them into rings.
  const segments: [MetricPoint, MetricPoint][] = [];
  for (let iy = 0; iy < ny; iy++) {
    for (let ix = 0; ix < nx; ix++) {
      const x0 = minX + ix * cell;
      const y0 = minY + iy * cell;
      const x1 = x0 + cell;
      const y1 = y0 + cell;

      const vBL = field[at(ix, iy)];
      const vBR = field[at(ix + 1, iy)];
      const vTR = field[at(ix + 1, iy + 1)];
      const vTL = field[at(ix, iy + 1)];

      let code = 0;
      if (vBL < 0) code |= 1;
      if (vBR < 0) code |= 2;
      if (vTR < 0) code |= 4;
      if (vTL < 0) code |= 8;
      if (code === 0 || code === 15) continue;

      const bottom = () => interp(x0, y0, vBL, x1, y0, vBR);
      const right = () => interp(x1, y0, vBR, x1, y1, vTR);
      const top = () => interp(x1, y1, vTR, x0, y1, vTL);
      const left = () => interp(x0, y1, vTL, x0, y0, vBL);
      const push = (a: MetricPoint, b: MetricPoint) => segments.push([a, b]);

      switch (code) {
        case 1: push(left(), bottom()); break;
        case 2: push(bottom(), right()); break;
        case 3: push(left(), right()); break;
        case 4: push(right(), top()); break;
        case 6: push(bottom(), top()); break;
        case 7: push(left(), top()); break;
        case 8: push(top(), left()); break;
        case 9: push(top(), bottom()); break;
        case 11: push(top(), right()); break;
        case 12: push(right(), left()); break;
        case 13: push(right(), bottom()); break;
        case 14: push(bottom(), left()); break;
        // Saddles: resolve with the cell-centre value so the two strands
        // connect the way the underlying field actually runs.
        case 5: {
          const centre = (vBL + vBR + vTR + vTL) / 4;
          if (centre < 0) {
            push(left(), bottom());
            push(right(), top());
          } else {
            push(left(), top());
            push(right(), bottom());
          }
          break;
        }
        case 10: {
          const centre = (vBL + vBR + vTR + vTL) / 4;
          if (centre < 0) {
            push(top(), left());
            push(bottom(), right());
          } else {
            push(top(), right());
            push(bottom(), left());
          }
          break;
        }
        default:
          break;
      }
    }
  }

  if (!segments.length) return [];

  const starts = new Map<string, [MetricPoint, MetricPoint][]>();
  for (const seg of segments) {
    const k = key(seg[0]);
    const bucket = starts.get(k);
    if (bucket) bucket.push(seg);
    else starts.set(k, [seg]);
  }

  const used = new Set<[MetricPoint, MetricPoint]>();
  const rings: Ring[] = [];

  for (const seg of segments) {
    if (used.has(seg)) continue;
    const ring: Ring = [seg[0]];
    let current = seg;
    used.add(current);

    for (let guard = 0; guard < segments.length + 4; guard++) {
      ring.push(current[1]);
      const candidates = starts.get(key(current[1]));
      const next = candidates?.find((c) => !used.has(c));
      if (!next) break;
      used.add(next);
      current = next;
      if (key(current[1]) === key(ring[0])) {
        ring.push(current[1]);
        break;
      }
    }

    if (ring.length >= 4) rings.push(ring);
  }

  if (!rings.length) return [];

  // Outer boundaries and holes always wind oppositely, but which way round
  // depends on the marching-squares table, so derive it instead of assuming:
  // the largest ring is necessarily an outer boundary, and anything winding
  // the same way is too. Everything is then normalized to counter-clockwise
  // for GeoJSON's right-hand rule.
  const measured = rings.map((ring) => ({ ring, area: ringArea(ring) }));
  const largest = measured.reduce((best, current) =>
    Math.abs(current.area) > Math.abs(best.area) ? current : best
  );
  const outerSign = Math.sign(largest.area) || 1;
  return measured
    .filter((entry) => Math.sign(entry.area) === outerSign)
    .map((entry) => (entry.area < 0 ? [...entry.ring].reverse() : entry.ring));
};
